import { RegisterOptions, ValidationRule, ValidationValue } from 'react-hook-form';
import { IntlShape } from 'react-intl/src/types';

export type AllowedDefaultTypes = 'required' | 'min' | 'max' | 'maxLength' | 'minLength' | 'pattern';

export type FormControls = Pick<RegisterOptions, AllowedDefaultTypes>;

export type JRCFormControls = {
    email?: ValidationRule<boolean>;
};
export type AllowedTypes = AllowedDefaultTypes | keyof JRCFormControls;

const URL_REGEX = /^[a-z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;

/**
 * Produces a react-hook-form compatible validation object with default messages for each error type
 * @param rules rules applied to the field
 * @param intl translation object
 */
export const useFormControls = (
    rules: (FormControls & JRCFormControls) | undefined,
    intl: IntlShape,
): FormControls | undefined => {
    if (!rules) return undefined;
    return Object.fromEntries(
        Object.entries(rules)
            .map(([rule, validation]) => getJRCErrors(rule as AllowedTypes, validation, intl))
            .filter((v): v is getJRCErrorsReturnType => !!v),
    );
};

type getJRCErrorsReturnType = [
    AllowedTypes,
    {
        value: ValidationValue;
        message: string | React.ReactNode;
    },
];

/**
 * Produces a react-hook-form compatible validation object with default messages for each error type
 * @param rule rule applied to the field
 * @param validation validation object
 * @param intl translation object
 */
export const getJRCErrors = (
    rule: AllowedTypes,
    validation: ValidationRule,
    intl: IntlShape,
): getJRCErrorsReturnType | undefined => {
    const isObject = typeof validation === 'object' && !(validation instanceof RegExp);
    const value = isObject ? validation.value : validation;
    const message = isObject ? validation.message : undefined;

    switch (rule) {
        case 'required':
            return [
                rule,
                {
                    value,
                    message: message || intl.formatMessage({ id: 'GLOBAL_Form_Controls_Required' }),
                },
            ];
        case 'max':
            return [
                rule,
                {
                    value,
                    message: message || intl.formatMessage({ id: 'GLOBAL_FORM_CONTROLS_MaxValue' }, { max: value }),
                },
            ];
        case 'min':
            return [
                rule,
                {
                    value,
                    message: message || intl.formatMessage({ id: 'GLOBAL_FORM_CONTROLS_MinValue' }, { min: value }),
                },
            ];
        case 'maxLength':
            return [
                rule,
                {
                    value,
                    message: message || intl.formatMessage({ id: 'GLOBAL_Form_Controls_MaxLength' }, { max: value }),
                },
            ];
        case 'minLength':
            return [
                rule,
                {
                    value,
                    message: message || intl.formatMessage({ id: 'GLOBAL_Form_Controls_MinLength' }, { min: value }),
                },
            ];
        case 'pattern':
            // This is a safe exit but the error message should always be redefined for a regexp pattern
            return [
                rule,
                {
                    value,
                    message:
                        message ||
                        intl.formatMessage({ id: 'GLOBAL_Form_Controls_Pattern' }, { pattern: value?.toString }),
                },
            ];
        case 'email':
            return [
                'pattern',
                {
                    value: URL_REGEX,
                    message: message || intl.formatMessage({ id: 'ERROR_Mail_Invalid' }),
                },
            ];
    }
};
